import { FunctionComponent, useEffect } from 'react';
import { InjectedIntl } from 'react-intl';
import { GenericProps } from 'types/interfaces';
import { EMPLOYMENT_VALUES } from './constants';
import { getTextFactory } from 'utils/TextUtils';
import { createAddressErrorFormatter, createErrorFormatter, createStockSymbolFormatter } from 'utils/productFormUtils';
import React from 'react';
import { Col, Row } from 'react-flexbox-grid';
import Collection from 'components/Collection';
import { FormGroup } from '@nbc-design/form-group';
import { ProductFormRadioBoolean, ProductFormStockSymbolSelect } from 'components/productFormFields';
import IndividualFirmEmployeeFormGroup from './components/IndividualFirmEmployeeFormGroup';
import FamilyFirmEmployeeFormGroup from './components/FamilyFirmEmployeeFormGroup';
import ProfessionalFormGroup from './components/ProfessionalFormGroup';
import ReferenceProfessionalFormGroup from './components/ReferenceProfessionalFormGroup';
import InvestmentFirmEmployeeTooltip from './components/InvestmentFirmEmployeeTooltip';
import { ProInvolvement } from '../../types';
import Heading from 'components/Heading';
import './styles.scss';
import { isNullOrUndefined } from 'utils';
import { useKycQuestions } from '../../../../hooks/useKycQuestion/useKycQuestion';
import { KycQuestionCodes, KycQuestionKeys } from '../../../../hooks/useKycQuestion/constants';
import { renderLabelWithBold } from '../../utils';

type FinancialInvolvementProps = GenericProps & {
  intl: InjectedIntl;
  proInvolvement?: ProInvolvement;
  firmEmployeeQuestionInd?: boolean;
  professionalQuestionInd?: boolean;
  productForm?: import('types/interfaces').ProductForm;
};

const FinancialInvolvement: FunctionComponent<FinancialInvolvementProps> = (props: FinancialInvolvementProps) => {
  const {
    intl,
    proInvolvement,
    firmEmployeeQuestionInd,
    professionalQuestionInd,
    change,
  }: FinancialInvolvementProps = props;

  const getText = getTextFactory(intl, 'employmentV2');
  const getGlobalText = getTextFactory(intl, 'global');
  const formatErrorMessage = (prefix?: string) => {
    if (prefix === 'address') {
      return createAddressErrorFormatter(intl);
    } else if (prefix === 'symbol') {
      return createStockSymbolFormatter(intl);
    } else {
      return createErrorFormatter(intl, prefix);
    }
  };

  const {
    firmEmployeeQuestionInd: kycFirmEmployee,
    professionalQuestionInd: kycProfessional,
    hasAdminInvolvement: kycHasAdminInvolvement,
    hasTenPercentInvolvement: kycHasTenPercentInvolvement,
    hasTwentyPercentInvolvement: kycHasTwentyPercentInvolvement,
  } = useKycQuestions({
    fieldMap: {
      firmEmployeeQuestionInd: KycQuestionCodes.employeeIndicator,
      professionalQuestionInd: KycQuestionCodes.professionalIndicator,
      hasAdminInvolvement: KycQuestionCodes.hasAdminInvolvement,
      hasTenPercentInvolvement: KycQuestionCodes.hasTenPercentInvolvement,
      hasTwentyPercentInvolvement: KycQuestionCodes.hasTwentyPercentInvolvement,
    },
    productForm: props.productForm,
    locale: intl.locale,
    valuesKey: KycQuestionKeys.INVESTOR_KYC_QUESTIONS,
    fallbackTitles: {
      hasAdminInvolvement: getText('hasAdminInvolvement'),
      hasTenPercentInvolvement: getText('hasTenPercentInvolvement'),
      hasTwentyPercentInvolvement: getText('hasTwentyPercentInvolvement'),
    },
  });

  const handleInvolvementChange = (listFieldName: string) => (isInvolved: boolean) => {
    change(listFieldName, isInvolved ? [null] : []);
  };

  // Initialize PRO/EMP questions indicators based on proInvolvement data
  useEffect(() => {
    const firmEmployeeValue =
      (proInvolvement?.investmentFirmEmployeeInd || proInvolvement?.familyInvestmentFirmEmployeeInd) ?? null;
    const professionalValue = (proInvolvement?.professionalInd || proInvolvement?.referenceProfessionalInd) ?? null;

    change('firmEmployeeQuestionInd', firmEmployeeValue);
    change('professionalQuestionInd', professionalValue);

    isNullOrUndefined(proInvolvement?.investmentFirmEmployeeInd) &&
      change('proInvolvement.investmentFirmEmployeeInd', false);
    isNullOrUndefined(proInvolvement?.familyInvestmentFirmEmployeeInd) &&
      change('proInvolvement.familyInvestmentFirmEmployeeInd', false);
    isNullOrUndefined(proInvolvement?.professionalInd) && change('proInvolvement.professionalInd', false);
    isNullOrUndefined(proInvolvement?.referenceProfessionalInd) &&
      change('proInvolvement.referenceProfessionalInd', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear related fields when questions are answered with 'No'
  useEffect(() => {
    if (firmEmployeeQuestionInd === false) {
      change('proInvolvement.investmentFirmEmployeeInd', false);
      change('proInvolvement.familyInvestmentFirmEmployeeInd', false);
      change('proInvolvement.familyInvestmentFirmEmployeeTypeCd', null);
      change('proInvolvement.familyInvestmentFirmEmployeeFullName', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmEmployeeQuestionInd]);

  useEffect(() => {
    if (professionalQuestionInd === false) {
      change('proInvolvement.professionalInd', false);
      change('proInvolvement.professionalSecuritiesFirmName', null);

      change('proInvolvement.referenceProfessionalInd', false);
      change('proInvolvement.referenceProfessionalRelationTypeCd', null);
      change('proInvolvement.referenceProfessionalRelationTypeDetails', null);
      change('proInvolvement.referenceProfessionalFullName', null);
      change('proInvolvement.referenceProfessionalSecuritiesFirmName', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionalQuestionInd]);

  return (
    <>
      {/* Professional in the securities industry */}
      <Heading type={'h2'} size={4}>
        {getText('hasBrokerageInvolvement')}
      </Heading>

      {/* EMP: Investment Firm Employee */}
      <Row>
        <Col xs={12} md={12} lg={12} data-test="question_firmEmployeeQuestionInd">
          <ProductFormRadioBoolean
            name="firmEmployeeQuestionInd"
            yesLabel={getGlobalText('button.yes')}
            noLabel={getGlobalText('button.no')}
            inline
            label={renderLabelWithBold(
              kycFirmEmployee.title || getText('firmEmployeeQuestion'),
              getText('firmEmployeeQuestion.bold'),
              <span style={{ display: 'inline-block' }}>
                <InvestmentFirmEmployeeTooltip getText={getText} />
              </span>,
            )}
            formatErrorMessage={formatErrorMessage()}
            className="employment__involvement-question"
          />
        </Col>
        {firmEmployeeQuestionInd && (
          <>
            <IndividualFirmEmployeeFormGroup
              {...proInvolvement}
              getText={getText}
              formatErrorMessage={formatErrorMessage()}
            />
            <FamilyFirmEmployeeFormGroup
              {...proInvolvement}
              change={change}
              getText={getText}
              getGlobalText={getGlobalText}
              formatErrorMessage={formatErrorMessage()}
            />
          </>
        )}
      </Row>

      {/* PRO: Another firm */}
      <Row>
        <Col xs={12} md={12} lg={12} data-test="field_professionalQuestionInd">
          <ProductFormRadioBoolean
            name="professionalQuestionInd"
            yesLabel={getGlobalText('button.yes')}
            noLabel={getGlobalText('button.no')}
            helpText={getText('hasProInvolvementHelpText')}
            inline
            label={renderLabelWithBold(
              kycProfessional.title || getText('professionalQuestion'),
              getText('professionalQuestion.bold'),
            )}
            formatErrorMessage={formatErrorMessage()}
            className="employment__involvement-question"
          />
        </Col>
        {professionalQuestionInd && (
          <>
            <Col xs={12} md={12} lg={12} data-test="otherFirmAdditionInfo">
              <p className="mt-0 mb-6">{getText('otherFirmAdditionInfo')}</p>
            </Col>
            <ProfessionalFormGroup
              {...proInvolvement}
              change={change}
              getText={getText}
              formatErrorMessage={formatErrorMessage()}
            />
            <ReferenceProfessionalFormGroup
              {...proInvolvement}
              change={change}
              getText={getText}
              getGlobalText={getGlobalText}
              formatErrorMessage={formatErrorMessage()}
            />
          </>
        )}
      </Row>

      {/* Insider, control block */}
      <Heading type={'h2'} size={4}>
        {getText('insider')}
      </Heading>
      {[
        {
          fieldName: 'hasAdminInvolvement',
          listName: 'adminInvolvementList',
          kycLabel: kycHasAdminInvolvement.title,
        },
        {
          fieldName: 'hasTenPercentInvolvement',
          listName: 'tenPercentInvolvementList',
          kycLabel: kycHasTenPercentInvolvement.title,
        },
        {
          fieldName: 'hasTwentyPercentInvolvement',
          listName: 'twentyPercentInvolvementList',
          kycLabel: kycHasTwentyPercentInvolvement.title,
        },
      ].map(({ fieldName, listName, kycLabel }) => (
        <React.Fragment key={`${fieldName}`}>
          <Row>
            <Col xs={12} md={12} lg={12} data-test={`field_${fieldName}`}>
              <ProductFormRadioBoolean
                name={fieldName}
                yesLabel={getGlobalText('button.yes')}
                noLabel={getGlobalText('button.no')}
                inline
                label={kycLabel}
                formatErrorMessage={formatErrorMessage()}
                className="employment__involvement-question"
                onChange={handleInvolvementChange(listName)}
              />
            </Col>
          </Row>

          {props[fieldName] === EMPLOYMENT_VALUES.TRADING_INVOLVEMENT_YES && (
            <Row className="collection-wrapper">
              <Col xs={12} md={12} lg={12} data-test={`field_${listName}`}>
                <FormGroup
                  label={{
                    text: getText(listName),
                    htmlFor: `symbol-autocomplete-${listName}[0]`,
                  }}
                  className="help-text"
                >
                  {null}
                </FormGroup>
                <Collection
                  data={props[listName]}
                  name={listName}
                  label={getText(listName)}
                  placeholder={getGlobalText('search')}
                  formatErrorMessage={formatErrorMessage()}
                  typeComponent={ProductFormStockSymbolSelect}
                  change={change}
                  defaultValue={null}
                  addLabel={getText('addSymbol')}
                />
              </Col>
            </Row>
          )}
        </React.Fragment>
      ))}
    </>
  );
};

export default FinancialInvolvement;
